import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateControlGroupMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetControlGroupsFlatDocument,
  namedOperations,
  UpdateControlGroupDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateControlGroupInput = {
  Id: string;
  Title: string;
  Description: string;
  Owner: string;
  OriginalTimestamp: string;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateControlGroup = createMutationHook({
  name: 'updateControlGroup',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.controlGroup.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateControlGroupInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.controlGroup.controlGroupById.queryKey(),
    trpc.frontend.controlGroup.controlGroups.queryKey(),
    trpc.frontend.controlGroup.controlGroupsByTitle.queryKey(),
    trpc.frontend.controlGroup.register.queryKey(),
  ],
  // trpc throws an error if no rows are affected (NotFound/Conflict)
  mapResponse: (): UpdateControlGroupMutation => ({
    update_control_group: {
      __typename: 'control_group_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateControlGroupDocument,
  graphqlVariables: (input: UpdateControlGroupInput) => ({
    Id: input.Id,
    Title: input.Title,
    Description: input.Description,
    Owner: input.Owner,
    OriginalTimestamp: input.OriginalTimestamp,
    CustomAttributeData: input.CustomAttributeData ?? null,
  }),
  evictFields: ['control_group', 'control_group_aggregate'],
  refetchQueries: [
    namedOperations.Query.getControlGroupById,
    { query: GetControlGroupsFlatDocument },
  ],
  awaitRefetchQueries: true,
});
